import {ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal} from '@angular/core';
import {FormBuilder, FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, RedirectCommand, ResolveFn, Router, RouterLink} from '@angular/router';
import {catchError, map, of} from 'rxjs';

import {CacheInvalidationStatus, ManagedPage, ManagedPagesApiService} from './managed-pages-api';
import {DasLayoutFormComponent} from '@dwa/daslayout-editor';

import {MatButton} from '@angular/material/button';
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatError, MatFormField, MatHint, MatInput, MatLabel} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatButtonToggle, MatButtonToggleGroup} from '@angular/material/button-toggle';
import {MatIcon} from '@angular/material/icon';
import {MatOption, MatSelect} from '@angular/material/select';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import {take} from 'rxjs/operators';
import {ApiErrorAdapterService, ApiErrorComponent, UiBackendError} from '../../components/backend-error-handler';
import {MiddleEllipsisPipe} from '../../pipes/middle-ellipsis';
import {DatePipe} from '@angular/common';
import {SnackbarService} from '../../../../core/services/snackbar.service';
import {toSignal} from '@angular/core/rxjs-interop';
import {normalizeQueryPath} from './query-path-utils';

export const managedPageResolver: ResolveFn<ManagedPage | null> = (route) => {
  const id = Number(route.paramMap.get('managedPageId'));
  const router = inject(Router);

  return inject(ManagedPagesApiService)
    .getById(id)
    .pipe(catchError(() => of(new RedirectCommand(router.parseUrl('/content/managed-pages')))));
};


@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    DasLayoutFormComponent,
    RouterLink,
    MatButton,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatFormField,
    MatFormFieldModule,
    MatLabel,
    MatInput,
    MatError,
    MatHint,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatIcon,
    MatSelect,
    MatOption,
    ApiErrorComponent,
    MiddleEllipsisPipe,
    DatePipe,
  ],
  template: `
    @let _pageMode = pageMode();
    @let _queryPathPrefix = queryPathPrefix();

    <header class="flex items-end justify-between">
      <a mat-button routerLink="/content/managed-pages">
        <mat-icon class="text-gray-700!">arrow_back</mat-icon>
        <span class="text-gray-700 font-normal tracking-normal">
        Back to all pages
          </span>
      </a>

      <div>
        @if (_pageMode === 'existing') {
          <div class="mb-1 text-right text-xs text-gray-600">
            Last modified: {{ managedPage().lastModifiedDate | date: 'dd.MM.yyyy HH:mm' }}
          </div>
        }
        <div class="flex justify-end gap-2">
          @if (_pageMode === 'existing') {
            <button class="w-24" mat-stroked-button color="warn" (click)="openDeleteDialog()">
              <mat-icon color="warn">delete</mat-icon>
              Delete
            </button>

            <button (click)="reset()" mat-stroked-button class="w-24" [disabled]="form.pristine">
              <mat-icon>refresh</mat-icon>
              Reset
            </button>
          }

          <button
            mat-stroked-button
            class="button-primary w-24"
            [disabled]="form.invalid || form.pristine || isSaving()"
            (click)="save()"
          >
            {{ isSaving() ? 'Saving...' : _pageMode === 'existing' ? 'Update' : 'Create' }}
          </button>
        </div>
      </div>
    </header>

    @if (saveError(); as saveError) {
      <mlw-api-error [error]="saveError" class="mt-4 block"/>
    }

    <form [formGroup]="form">
      <mat-card appearance="outlined" class="my-5">
        <mat-card-header>
          <mat-card-title>Base information</mat-card-title>
        </mat-card-header>

        <mat-card-content>
          <div class="mt-6 grid max-w-5xl grid-cols-6 gap-x-4 gap-y-6">
            <mat-form-field class="col-span-4" appearance="outline">
              <mat-label>Full URL Path</mat-label>

              @if (_queryPathPrefix) {
                <span
                  matPrefix
                  class="mx-2 inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-sm whitespace-nowrap text-gray-600"
                  [title]="'Parent URL: ' + _queryPathPrefix"
                >
                  {{ _queryPathPrefix | middleEllipsis: 40 }}/
                </span>
              }

              <input
                matInput
                formControlName="queryPath"
                [placeholder]="_queryPathPrefix ? 'e.g. datenschutz' : 'e.g. rechtliches/impressum'"
              />

              <mat-hint>
                @if (_queryPathPrefix) {
                  The page will be created under this parent URL. Enter only the last part, for example "datenschutz".
                } @else {
                  Enter the page URL, for example "impressum".
                }
              </mat-hint>

              @if (form.controls.queryPath.invalid && form.controls.queryPath.touched) {
                <mat-error>
                  @if (_queryPathPrefix) {
                    Please enter the remaining path.
                  } @else {
                    Query Path is required.
                  }
                </mat-error>
              }
            </mat-form-field>

            <mat-button-toggle-group
              class="col-span-2 h-12 w-fit justify-self-end"
              formControlName="published"
              aria-label="Publication state"
            >
              <mat-button-toggle [value]="false">Draft</mat-button-toggle>
              <mat-button-toggle [value]="true">Published</mat-button-toggle>
            </mat-button-toggle-group>

            @if (_queryPathPrefix) {
              <div class="text-primary border-primary col-span-6 rounded-md border px-3 py-3">
                <div class="flex items-start gap-2">
                  <mat-icon class="mt-0.5 h-4! w-4! text-[16px]!">info</mat-icon>

                  <div class="min-w-0">
                    <div class="text-xs font-semibold tracking-wide uppercase">Final URL path</div>
                    <div class="mt-1 text-sm">This exact path will be saved.</div>
                    <div class="mt-2 rounded font-mono text-sm break-all text-gray-700" [title]="resolvedQueryPath()">
                      {{ resolvedQueryPath() || '—' }}
                    </div>
                  </div>
                </div>
              </div>
            }

            <mat-form-field class="col-span-6" appearance="outline">
              <mat-label>Title</mat-label>
              <input matInput formControlName="title" placeholder="Enter the page title"/>
              <mat-hint>This title appears in the browser tab and in search results.</mat-hint>
            </mat-form-field>

            <mat-form-field class="col-span-6" appearance="outline">
              <mat-label>Description</mat-label>
              <textarea
                matInput
                formControlName="description"
                placeholder="Enter a short description of the page"
              ></textarea>
              <mat-hint>This description helps users find your page in search engines.</mat-hint>
            </mat-form-field>

            <mat-form-field class="col-span-6" appearance="outline">
              <mat-label>Application</mat-label>
              <mat-select formControlName="application">
                <mat-option value="DWA">DWA</mat-option>
                <mat-option value="GWPLUS">GWPLUS</mat-option>
              </mat-select>
              @if (form.controls.application.invalid && form.controls.application.touched) {
                <mat-error>Application is required</mat-error>
              }
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <mat-card appearance="outlined">
        <mat-card-header>
          <mat-card-title>Markup</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <daslayout-editor formControlName="html"/>
        </mat-card-content>
      </mat-card>
    </form>
  `,
})
export class ManagedPagesDetailComponent {
  private readonly api = inject(ManagedPagesApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly apiErrorAdapter = inject(ApiErrorAdapterService);
  private readonly snackbarService = inject(SnackbarService);

  protected readonly isSaving = signal(false);
  protected readonly saveError = signal<UiBackendError | null>(null);

  private readonly _resolvedMangedPage = toSignal<ManagedPage | null>(
    this.route.data.pipe(map((data) => data.managedPage || null)),
  );
  protected readonly managedPage = linkedSignal(() => this._resolvedMangedPage());
  protected readonly queryPathPrefix = toSignal(
    this.route.queryParamMap.pipe(map((params) => params.get('queryPathPrefix'))),
  );

  protected readonly pageMode = computed(() => (this.managedPage() ? 'existing' : 'new'));
  protected readonly form = this.buildForm();

  private readonly _currentQueryPath = toSignal(this.form.controls.queryPath.valueChanges);
  protected readonly resolvedQueryPath = computed(() => {
    return this.pageMode() === 'new'
      ? this.joinQueryPath(this.queryPathPrefix() ?? '', this._currentQueryPath() ?? '')
      : this._currentQueryPath();
  });

  protected save() {
    if (this.form.invalid || this.form.pristine || this.isSaving()) {
      return;
    }

    this.saveError.set(null);
    const raw = this.form.getRawValue();
    const queryPath =
      this.pageMode() === 'new' && this.queryPathPrefix()
        ? this.joinQueryPath(this.queryPathPrefix(), raw.queryPath)
        : raw.queryPath;

    const updateOrCreateRequest$ =
      this.pageMode() === 'existing'
        ? this.api.update(raw, this.managedPage().id)
        : this.api.create({...raw, queryPath});

    this.isSaving.set(true);
    updateOrCreateRequest$.pipe(take(1)).subscribe({
      next: (savedPage) => {
        this.isSaving.set(false);

        if (this.pageMode() === 'existing') {
          this.managedPage.set(savedPage);
          this.showSnackbar(savedPage.cacheInvalidationStatus, 'update');
          this.form.markAsPristine();
          this.form.markAsUntouched();
          return;
        }

        void this.router.navigate(['../', savedPage.id], {relativeTo: this.route});
      },
      error: (error: unknown) => {
        this.isSaving.set(false);
        this.saveError.set(
          this.apiErrorAdapter.toUiError(
            error,
            this.pageMode() === 'existing' ? 'The page could not be updated.' : 'The page could not be created.',
          ),
        );
      },
    });
  }

  protected reset() {
    const page = this.managedPage();
    if (!page) {
      throw new Error('Managed page must not be null');
    }

    // Workaround
    // TODO: Applies a workaround for a known issue (see [DWA-18443](https://jira.porscheinformatik.com/jira/browse/DWA-18443)).
    //       See also landing-page-detail component for a similar workaround and the related Angular issue:
    this.form.reset({html: ''});
    this.form.reset({
      queryPath: page.queryPath ?? '',
      title: page.title ?? '',
      description: page.description ?? '',
      html: page.html ?? '',
      published: !!page.published,
      application: page.application ?? 'DWA',
    });
  }

  protected openDeleteDialog() {
    const page = this.managedPage();
    if (!page) {
      throw new Error('Managed page must not be null');
    }

    const dialogRef = this.dialog.open(ManagedPageConfirmDeleteComponent, {
      width: '600px',
      data: {title: page.title, queryPath: page.queryPath, id: page.id},
    });

    dialogRef.afterClosed().subscribe((deleteConfirmed) => {
      if (!deleteConfirmed) {
        return;
      }

      this.api
        .delete(page.id)
        .pipe(take(1))
        .subscribe({
          next: (cacheInvalidationStatus) => {
            void this.router.navigate(['../'], {relativeTo: this.route});
            this.showSnackbar(cacheInvalidationStatus, 'delete');
          },
          error: (error: unknown) => {
            this.saveError.set(this.apiErrorAdapter.toUiError(error, 'The page could not be deleted.'));
          },
        });
    });
  }

  private buildForm() {
    const page = this.managedPage();
    return this.fb.group({
      queryPath: new FormControl(page?.queryPath ?? '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      title: new FormControl(page?.title ?? '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      description: new FormControl(page?.description ?? '', {
        nonNullable: true,
      }),
      html: new FormControl(page?.html ?? '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      published: new FormControl(!!page?.published, {
        nonNullable: true,
      }),
      application: new FormControl<ManagedPage['application']>(
        {value: page?.application ?? 'DWA', disabled: true},
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
    });
  }

  private joinQueryPath(prefix: string, path: string): string {
    const normalizedPrefix = normalizeQueryPath(prefix);
    const normalizedPath = normalizeQueryPath(path);

    if (!normalizedPrefix) {
      return normalizedPath;
    }

    if (!normalizedPath) {
      return normalizedPrefix;
    }

    return `${normalizedPrefix}/${normalizedPath}`;
  }

  private showSnackbar(cacheInvalidationStatus: CacheInvalidationStatus, action: 'update' | 'delete'): void {
    const allCleared = ManagedPagesApiService.allCacheInvalidationStatusesCleared(cacheInvalidationStatus);
    const title = action === 'update'
      ? 'Managed page successfully updated.'
      : 'Managed page successfully deleted.';

    if (!allCleared) {
      console.error('Cache invalidation statuses indicate that not all caches were cleared:', cacheInvalidationStatus);
      const description = action === 'update'
        ? 'Page updated, but changes may not be immediately visible due to cache propagation.'
        : 'Page deleted, but cached versions may still be accessible for a short time.';

      this.snackbarService.openWithDescription(
        title,
        description,
        true,
        10000
      );
    } else {
      this.snackbarService.openSnackBar(title);
    }
  }
}

@Component({
  standalone: true,
  imports: [MatButton, MatDialogTitle, MatDialogContent, MatDialogActions],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h2 mat-dialog-title [title]="data.title">Delete managed page?</h2>

    <mat-dialog-content>
      <p class="mb-3">You are about to permanently delete this managed page. This action cannot be undone.</p>

      <div class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
        <div class="mb-2">
          <div class="text-xs font-medium tracking-wide text-gray-500 uppercase">Title</div>
          <div class="truncate text-gray-900" [title]="data.title">
            {{ data.title }}
          </div>
        </div>

        <div class="mb-2">
          <div class="text-xs font-medium tracking-wide text-gray-500 uppercase">Full URL Path</div>
          <div class="truncate font-mono text-gray-900" [title]="data.queryPath">
            {{ data.queryPath }}
          </div>
        </div>

        <div>
          <div class="text-xs font-medium tracking-wide text-gray-500 uppercase">ID</div>
          <div class="truncate font-mono text-gray-900" [title]="data.id">
            {{ data.id }}
          </div>
        </div>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-raised-button color="warn" (click)="onConfirm()">Delete</button>
    </mat-dialog-actions>
  `,
})
class ManagedPageConfirmDeleteComponent {
  private readonly dialogRef = inject(MatDialogRef<ManagedPageConfirmDeleteComponent>);
  protected readonly data: { id: number; title: string; queryPath: string } = inject(MAT_DIALOG_DATA);

  protected onCancel(): void {
    this.dialogRef.close(false);
  }

  protected onConfirm(): void {
    this.dialogRef.close(true);
  }
}
